/**
 * Competition Manager Training — Setup & Invite Team
 * Starts on the Setup page, demonstrates inviting 3 judges and a producer.
 * 600 frames @ 30fps = 20 seconds.
 *
 * Timeline:
 *  0-50       Title card
 *  50-590     Setup page walkthrough:
 *    50-120   Overview of the setup page (divisions, heats, settings, invite)
 *    120-200  Type judge1 email, check Judge, click Invite
 *    200-270  Type judge2 email, click Invite
 *    270-340  Type judge3 email, click Invite
 *    340-430  Type producer email, check Producer (uncheck Judge), click Invite
 *    430-590  Show all 4 invitations, done
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../../components/AutoCursor";
import { ScreenMock } from "../../components/ScreenMock";
import { FadeTransition } from "../../components/FadeTransition";
import { StepLabel } from "../../components/StepLabel";

const MONO = "'JetBrains Mono', monospace";

export function CreateProjectGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={50}><Title /></Sequence>
      <Sequence from={40} durationInFrames={510}>
        <FadeTransition durationInFrames={510} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={545} durationInFrames={55}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 35, 50], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: "#a855f7", textTransform: "uppercase", letterSpacing: 3, fontFamily: MONO, marginBottom: 14 }}>Competition Manager · Setup</div>
      <div style={{ fontSize: 48, fontWeight: 800, color: "#fff" }}>Invite Your Team</div>
    </AbsoluteFill>
  );
}

function Demo() {
  const frame = useCurrentFrame();

  // Invitation state — each invite appears after the cursor clicks Invite
  const invites: Array<{ email: string; role: string; status: string }> = [];
  if (frame >= 165) invites.push({ email: "sarah@example.com", role: "Judge", status: "Pending" });
  if (frame >= 240) invites.push({ email: "mike@example.com", role: "Judge", status: "Pending" });
  if (frame >= 310) invites.push({ email: "lily@example.com", role: "Judge", status: "Pending" });
  if (frame >= 395) invites.push({ email: "james@example.com", role: "Producer", status: "Pending" });

  // Current typing state
  const typingEmail =
    frame >= 120 && frame < 165 ? "sarah@example.com".slice(0, Math.min(Math.floor((frame - 120) / 2), 17)) :
    frame >= 195 && frame < 240 ? "mike@example.com".slice(0, Math.min(Math.floor((frame - 195) / 2), 16)) :
    frame >= 270 && frame < 310 ? "lily@example.com".slice(0, Math.min(Math.floor((frame - 270) / 2), 16)) :
    frame >= 340 && frame < 395 ? "james@example.com".slice(0, Math.min(Math.floor((frame - 340) / 2), 17)) :
    "";

  const isProducerRole = frame >= 355 && frame < 400;

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock/setup">
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ ...navLink, color: "#e4e4e7" }}>COMPETITIONS</span>
            <span style={navLink}>RIDERS</span>
            <span style={navLink}>TRAINING</span>
            <span style={navLink}>SUPPORT</span>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#71717a" }}>Takoda Mundy</span>
          </div>
          <div style={{ padding: "20px 32px" }}>
            {/* Page header */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Setup: BOARDSTOCK</div>
              <div style={{ fontSize: 13, color: "#71717a", marginTop: 4 }}>Configure divisions, heats, assign riders, invite judges</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {/* Left column — Divisions + Heats (static, just for context) */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Divisions */}
                <div style={cardStyle}>
                  <div style={cardTitle}>Divisions</div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                    <div style={{ ...inputStyle, flex: 1, color: "#52525b" }}>Division name (e.g. Men</div>
                    <div style={btnPrimary}>Add</div>
                  </div>
                  {["Men's", "Women's"].map((d) => (
                    <div key={d} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #1a1a1e" }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{d}</span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <span style={{ fontSize: 11, color: "#71717a" }}>Edit</span>
                        <span style={{ fontSize: 11, color: "#ef4444", border: "1px solid #ef4444", padding: "1px 8px" }}>Delete</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Heats */}
                <div style={cardStyle}>
                  <div style={cardTitle}>Heats</div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    <div style={{ ...inputStyle, flex: 1, color: "#52525b" }}>Heat name...</div>
                    <div style={btnPrimary}>Add</div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 600, color: "#52525b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, fontFamily: MONO }}>Division</div>
                      <div style={{ ...inputStyle, width: 160, color: "#52525b", fontSize: 12 }}>Select a division...</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, fontWeight: 600, color: "#52525b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, fontFamily: MONO }}>Max Attempts</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 140, height: 4, background: "#2a2a32", borderRadius: 2, position: "relative" }}>
                          <div style={{ position: "absolute", left: 0, top: -4, width: 12, height: 12, borderRadius: "50%", background: "#3b82f6" }} />
                        </div>
                        <span style={{ fontSize: 12, fontFamily: MONO, color: "#e4e4e7" }}>1</span>
                      </div>
                    </div>
                  </div>
                  {/* Existing heats */}
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#52525b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, fontFamily: MONO }}>MEN'S</div>
                  {["Finals", "Heat 1"].map((h) => (
                    <div key={h} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #1a1a1e" }}>
                      <span style={{ fontSize: 11, color: "#71717a" }}>▶</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{h}</span>
                      <span style={{ marginLeft: "auto", fontSize: 9, fontFamily: MONO, color: "#52525b", background: "#1a1a1e", padding: "1px 6px" }}>MAX: 10</span>
                      <span style={{ fontSize: 9, fontFamily: MONO, color: "#52525b", background: "#1a1a1e", padding: "1px 6px" }}>{h === "Finals" ? "4 ATTEMPTS" : "3 ATTEMPTS"}</span>
                      <span style={{ fontSize: 9, color: "#ef4444", border: "1px solid #ef4444", padding: "1px 6px" }}>DELETE</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#52525b", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 12, marginBottom: 6, fontFamily: MONO }}>WOMEN'S</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #1a1a1e" }}>
                    <span style={{ fontSize: 11, color: "#71717a" }}>▶</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Finals</span>
                    <span style={{ marginLeft: "auto", fontSize: 9, fontFamily: MONO, color: "#52525b", background: "#1a1a1e", padding: "1px 6px" }}>MAX: 10</span>
                    <span style={{ fontSize: 9, fontFamily: MONO, color: "#52525b", background: "#1a1a1e", padding: "1px 6px" }}>3 ATTEMPTS</span>
                    <span style={{ fontSize: 9, color: "#ef4444", border: "1px solid #ef4444", padding: "1px 6px" }}>DELETE</span>
                  </div>
                </div>
              </div>

              {/* Right column — Settings + Invite Team */}
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* Competition Settings */}
                <div style={cardStyle}>
                  <div style={cardTitle}>Competition Settings</div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#52525b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4, fontFamily: MONO }}>Scoring Model</div>
                  <div style={{ ...inputStyle, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span>Best Score (Max)</span>
                    <span style={{ fontSize: 10, color: "#52525b" }}>▼</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#71717a", marginBottom: 8 }}>Rider keeps only their highest judge score</div>
                  <div style={{ fontSize: 11, color: "#71717a" }}>Judges: 3 (set at creation, cannot be changed)</div>
                </div>

                {/* Invite Team — this is the main interactive area */}
                <div style={{ ...cardStyle, border: "1px solid #2a2a32" }}>
                  <div style={cardTitle}>Invite Team</div>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    <div id="invite-email" style={{ ...inputStyle, flex: 1, color: typingEmail ? "#e4e4e7" : "#52525b" }}>
                      {typingEmail || "Email address"}
                      {typingEmail && <span style={{ color: "#3b82f6" }}>|</span>}
                    </div>
                    <div id="invite-btn" style={btnPrimary}>Invite</div>
                  </div>
                  {/* Role checkboxes */}
                  <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div id="check-judge" style={{ width: 14, height: 14, border: `1px solid ${!isProducerRole ? "#22c55e" : "#2a2a32"}`, background: !isProducerRole ? "#22c55e" : "#0a0a0b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {!isProducerRole && <span style={{ fontSize: 10, color: "#fff" }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 12, color: "#e4e4e7" }}>Judge</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div id="check-producer" style={{ width: 14, height: 14, border: `1px solid ${isProducerRole ? "#a855f7" : "#2a2a32"}`, background: isProducerRole ? "#a855f7" : "#0a0a0b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {isProducerRole && <span style={{ fontSize: 10, color: "#fff" }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 12, color: "#e4e4e7" }}>Producer</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 14, height: 14, border: "1px solid #2a2a32", background: "#0a0a0b" }} />
                      <span style={{ fontSize: 12, color: "#e4e4e7" }}>Admin</span>
                    </div>
                  </div>

                  {/* Invitation list */}
                  {invites.map((inv) => (
                    <div key={inv.email} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #1a1a1e" }}>
                      <span style={{ fontSize: 12, fontFamily: MONO, color: "#94a3b8" }}>{inv.email}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 8px", background: inv.role === "Judge" ? "#22c55e" : "#a855f7", color: "#fff" }}>{inv.role}</span>
                      <span style={{ fontSize: 11, color: "#71717a" }}>{inv.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <AutoCursor points={[
          { x: 960, y: 400, frame: 0 },
          // Look around the page
          { x: 960, y: 400, frame: 70 },
          // Click email field, type judge 1
          { target: "invite-email", frame: 100 },
          { target: "invite-email", frame: 120, click: true },
          // Click Invite for judge 1
          { target: "invite-btn", frame: 150 },
          { target: "invite-btn", frame: 165, click: true },
          // Type judge 2
          { target: "invite-email", frame: 185 },
          { target: "invite-email", frame: 195, click: true },
          { target: "invite-btn", frame: 225 },
          { target: "invite-btn", frame: 240, click: true },
          // Type judge 3
          { target: "invite-email", frame: 260 },
          { target: "invite-email", frame: 270, click: true },
          { target: "invite-btn", frame: 298 },
          { target: "invite-btn", frame: 310, click: true },
          // Switch to Producer role
          { target: "check-producer", frame: 340 },
          { target: "check-producer", frame: 355, click: true },
          // Type producer email
          { target: "invite-email", frame: 365 },
          { target: "invite-email", frame: 375, click: true },
          { target: "invite-btn", frame: 385 },
          { target: "invite-btn", frame: 395, click: true },
          // Done — rest
          { x: 960, y: 500, frame: 440 },
          { x: 960, y: 500, frame: 500 },
        ]} />
      </ScreenMock>

      <StepLabel text="The Setup page shows divisions, heats, settings, and invites" show={5} hide={100} color="#a855f7" />
      <StepLabel text="Type an email and click Invite — Judge is selected by default" show={100} hide={180} color="#22c55e" />
      <StepLabel text="Invite as many judges as your competition needs" show={180} hide={325} color="#22c55e" />
      <StepLabel text="Switch to Producer role to invite a broadcast controller" show={325} hide={410} color="#a855f7" />
      <StepLabel text="All 4 team members invited — they'll get an email with a link" show={415} hide={500} color="#22c55e" />
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Team invited — judges and producers will get email links</div>
    </AbsoluteFill>
  );
}

/* ── Styles ── */
const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 48, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: MONO, fontWeight: 800, fontSize: 15, letterSpacing: 2 };
const navLink: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a" };
const cardStyle: React.CSSProperties = { background: "#111113", border: "1px solid #2a2a32", padding: 16 };
const cardTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 };
const inputStyle: React.CSSProperties = { padding: "8px 12px", background: "#0a0a0b", border: "1px solid #2a2a32", fontSize: 13 };
const btnPrimary: React.CSSProperties = { padding: "8px 14px", background: "#3b82f6", color: "#fff", fontSize: 12, fontWeight: 600, textTransform: "uppercase" };
